










































































































































import Vue from "vue";
import { Component } from "vue-property-decorator";

import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import VuePhoneNumberInput from "vue-phone-number-input";
import { RingLoader } from "vue-spinners-css";

import { BaseRootPageComponent } from "../BaseRootPageComponent";
import Modal from "@/components/Modal.vue";
import { TrustedDevice } from "@/models";
import _auth from "@/utils/auth";
import { OTP_LENGTH } from "@/utils/constants";
import _gateway from "@/utils/gateway";
import { updateGTMGlobalVariables } from "@/utils/gtm";
import Modals from "@/utils/modals";
import { getFullName } from "@/utils/names";
import { logError, SeverityLevel } from "@/utils/appInsights";

Vue.component("vue-phone-number-input", VuePhoneNumberInput);

export enum PhoneNumberActions {
  Add = "add",
  Update = "update",
  Remove = "remove"
}
@Component({
  components: { RingLoader, Modal }
})
export default class UserProfile extends BaseRootPageComponent {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  readonly phoneNumberActions = PhoneNumberActions;

  showOtp = false;
  error = "";
  submitting = false;
  loading = false;
  userPhone?: string = "";
  otp = "";
  otpLen = OTP_LENGTH;
  isNumberVerified = false;
  modalTitle = "";
  countryCode = "US";

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  user = this.auth.user!;

  isDisableNextBtn = false;
  trustedDeviceList: TrustedDevice[] | undefined = [];
  phoneNumberAction = PhoneNumberActions.Add;
  resendMsg = "";
  formattedNumber = "";
  userPhoneNumber?: string = "";

  showPhoneModal = false;

  created() {
    this.setTitle("User Profile");
    // updateGTMGlobalVariables(this);
  }

  async mounted() {
    await this.loadUserProfile();
  }

  get name() {
    return getFullName(this.user);
  }

  get email() {
    return this.user.email ?? "";
  }

  get phoneNumber() {
    if (!this.userPhoneNumber) return "";

    try {
      // If the phone number is not formatted correctly, the library will throw an error
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parseAndKeepRawInput(this.userPhoneNumber);
      this.countryCode = phoneUtil.getRegionCodeForNumber(number) || "US";
      return phoneUtil.format(number, PhoneNumberFormat.NATIONAL);
    } catch (error) {
      logError(this, error, SeverityLevel.Warning, {
        message: "Cannot parse user's phone number.",
        phoneNumber: this.userPhoneNumber
      });

      return "";
    }
  }

  get hasBiometricsHardware() {
    return this.$nativeApp.hasBiometricsHardware;
  }

  get isUsingBiometrics() {
    return this.$nativeApp.isUsingBiometrics;
  }

  async loadUserProfile() {
    // FYI if these APIs fail, just let unhandled error handler catch it (redirect to the error page)
    try {
      this.loading = true;
      this.trustedDeviceList = await this.gateway.getTrustedDevices();

      const profile = await this.gateway.getUserProfile();
      this.userPhoneNumber = profile.phoneNumber;
    } finally {
      this.loading = false;
    }
  }

  async removeBiometrics() {
    const proceed = await Modals.biometric.remove.showConfirmation(this);
    if (!proceed) return;

    // FYI if an error occurs, the uncaught error handler will redirect the user to the error page
    await this.gateway.removeBiometrics();
    this.$nativeApp.removeBiometrics();
  }

  resendOtp() {
    this.resendMsg = "";
    this.error = "";
    this.sendCodeToPhone(true);
  }

  phoneInputChange(input: string) {
    this.userPhone = input ? input.replace(/\s+/, "") : "";
    this.enableDisableNextButton();
  }

  openModal() {
    const action = this.userPhoneNumber ? PhoneNumberActions.Update : PhoneNumberActions.Add;

    if (action === this.phoneNumberActions.Add) {
      this.modalTitle = "Add Phone";
      this.userPhone = "";
      this.countryCode = "US";
    } else if (action === this.phoneNumberActions.Update) {
      this.modalTitle = "Change Phone";
      this.userPhone = this.userPhoneNumber;
    }

    this.showPhoneModal = true;
    this.phoneNumberAction = action;
    this.enableDisableNextButton();
  }

  closeModal() {
    this.showOtp = false;
    this.resendMsg = "";
    this.error = "";
    this.showPhoneModal = false;
  }

  sendCodeToPhone(fromResend?: boolean) {
    this.gateway.sendVerficationCodeToPhone(this.formattedNumber).then(
      () => {
        if (fromResend) {
          this.resendMsg = "OTP resent successfully";
        }
        this.showOtp = true;
        this.otp = "";
      },
      () => (this.error = "An unknown error occurred")
    );
  }

  updateUserPhone(action?: PhoneNumberActions) {
    const phoneNumber = action === PhoneNumberActions.Remove ? undefined : this.formattedNumber;

    this.gateway.updatePhoneNumber(phoneNumber).then(
      () => {
        this.isNumberVerified = true;
        this.showOtp = false;
        this.userPhoneNumber = action === PhoneNumberActions.Remove ? "" : this.formattedNumber;
        this.showPhoneModal = false;
      },
      () => {
        this.error = "An unknown error occurred";
      }
    );
  }

  validateCode() {
    this.gateway.validateCodeToAddOrUpdatePhone(this.otp).then(
      response => {
        if (response.isSuccess) {
          this.error = "";
          this.updateUserPhone();
          this.showPhoneModal = false;
        }
      },
      err => {
        this.error = err?.data?.errorMessage || "An unknown error occurred";
      }
    );
  }

  handleAddPhoneSubmit() {
    if (!this.showOtp) {
      if (!this.userPhone) {
        // Remove Phone Number - call Remove phone number API if phone field is cleared
        if (this.phoneNumberAction === this.phoneNumberActions.Update) {
          this.error = "";
          this.updateUserPhone(PhoneNumberActions.Remove);
        } else {
          // Add Phone number - show error
          this.error = "This field is required";
        }
      } else {
        try {
          // check if entered phone number is valid as per country code
          const phoneUtil = PhoneNumberUtil.getInstance();
          const number = phoneUtil.parseAndKeepRawInput(this.userPhone, this.countryCode);
          if (!(phoneUtil.isValidNumber(number) && phoneUtil.isPossibleNumber(number))) {
            this.error = `Please enter a valid ${this.countryCode} number`;
            return;
          }
        } catch (error) {
          console.log(error);
          return;
        }
        // Send verification to phone for valid entry
        this.sendCodeToPhone();
      }
    } else {
      if (!this.otp || this.otp.length !== this.otpLen) {
        this.error = "Please enter the 6 digit verification code";
        return;
      } else {
        this.validateCode();
      }
    }
  }

  enableDisableNextButton() {
    if (this.phoneNumberAction === this.phoneNumberActions.Update) {
      try {
        // If the phone number is not formatted correctly, the library will throw an error
        const phoneUtil = PhoneNumberUtil.getInstance();
        const phoneNumber = phoneUtil.parseAndKeepRawInput(this.userPhoneNumber || "");
        const currentNumber = phoneNumber.getNationalNumber()?.toString();

        if (this.userPhoneNumber && this.userPhone !== currentNumber) {
          this.isDisableNextBtn = false;
        } else {
          this.isDisableNextBtn = true;
        }
      } catch (error) {
        logError(this, error, SeverityLevel.Warning, {
          message: "Cannot parse user's phone number when checking against entered phone number.",
          phoneNumber: this.userPhoneNumber
        });

        // Don't disable the button when the original number is invalid, otherwise the user will never be able to enter
        // a new number
        this.isDisableNextBtn = false;
      }
    }
  }

  updatePhoneNumber(data: any) {
    this.resendMsg = "";
    this.countryCode = data.countryCode;
    this.error = "";
    if (data.isValid) {
      this.formattedNumber = data.formattedNumber;
    }
  }

  handleInputChange() {
    this.resendMsg = "";
    this.error = "";
  }

  async removeTrustedDevice(device: TrustedDevice) {
    const proceed = await Modals.showConfirm(
      this,
      `Are you sure you want to remove ${device.model || device.make}? This will turn off biometric login if enabled.`,
      {
        title: "Remove device",
        okTitle: "Remove",
        okVariant: "danger"
      }
    );
    if (!proceed) return;

    // FYI if this API fails, just let unhandled error handler catch it (redirect to the error page)
    await this.gateway.removeTrustedDevice(device.id);

    // When a trusted device is deleted from the backend, the biometric secret is also deleted and the native app should be updated to reflect this.
    if (this.isUsingBiometrics && this.auth.deviceGuid === device.guid) {
      this.$nativeApp.removeBiometrics();
    }

    await this.loadUserProfile();
  }
}
